// Business logic for the "huurders op deze plattegrond" list shown under the plan.
//
// Resolves which tenant ("huurder") holds each unit/garage that is placed (pin or contour)
// on a building plan, then groups the placements per tenant. Two data sources feed this:
//   - units     → the building's rental contracts (contract.unit + contract.contractContacts)
//   - attributes → the building attributes' contractProducts → contract → contractContacts
// Both already carry the tenant `resource.name` and `contactType` from the backend, so this
// stays a pure, side-effect-free derivation that is easy to unit-test.

import { ContactType, ContractPhase } from "shared/types/rental-contracts";
import { PlanAnnotation, PlanShape } from "./planTypes";

/** Group label for placed units/garages that have no active tenant (vacant / closed contract). */
export const NO_TENANT_LABEL = "Zonder huurder";

export interface PlanListItem {
  /** Stable key matching the canvas highlight key: `${targetType}:${id}`. */
  key: string;
  targetType: "unit" | "attribute";
  id: string;
  label: string;
  /** Status colour (green/amber/red), reused from the plan colours. */
  color: string;
  /** Which shapes are placed for this target (a target may have both a pin and a contour). */
  shapes: PlanShape[];
  /** First page the target appears on — used to jump to it when locating. */
  page: number;
}

export interface PlanTenantGroup {
  /** Tenant display name, or {@link NO_TENANT_LABEL} for placements without an active tenant. */
  tenant: string;
  isUnassigned: boolean;
  items: PlanListItem[];
  /**
   * All units this tenant rents in the building (by unitId), regardless of whether they are
   * placed on this plan. Lets a garage-plan reader see which apartment the garage tenant also
   * rents. Empty for the unassigned group.
   */
  rentedUnits: string[];
}

const annKey = (a: PlanAnnotation): string =>
  `${a.targetType}:${a.targetType === "unit" ? a.unitId : a.buildingAttribute?.id}`;

/** A contract still ties a tenant to a unit/garage as long as it is not fully closed. */
const isActivePhase = (phase: unknown): boolean =>
  typeof phase === "string" && phase !== ContractPhase.CLOSED;

/**
 * Distinct tenant ("Contractant (huurder)") display names on a set of contract contacts.
 * Co-tenants on the same contract each surface as their own name.
 */
export function tenantNamesFromContacts(contractContacts: any[] | undefined | null): string[] {
  if (!Array.isArray(contractContacts)) return [];
  const names = contractContacts
    .filter((c) => c?.contactType === ContactType.TENANT && c?.resource?.name)
    .map((c) => String(c.resource.name).trim())
    .filter(Boolean);
  return Array.from(new Set(names));
}

function setMapToArrays(map: Record<string, Set<string>>): Record<string, string[]> {
  return Object.fromEntries(Object.entries(map).map(([key, set]) => [key, Array.from(set)]));
}

/** unitId → tenant names, derived from the building's non-closed rental contracts. */
export function buildUnitTenantMap(contracts: any[] | undefined | null): Record<string, string[]> {
  const map: Record<string, Set<string>> = {};
  (contracts || []).forEach((contract) => {
    const unitId = contract?.unit?.id;
    if (!unitId || !isActivePhase(contract?.phase)) return;
    const names = tenantNamesFromContacts(contract?.contractContacts);
    if (names.length === 0) return;
    const key = String(unitId);
    if (!map[key]) map[key] = new Set();
    names.forEach((name) => map[key].add(name));
  });
  return setMapToArrays(map);
}

/**
 * Inverts a unitId → tenant-names map into tenant-name → unitIds, so we can list every unit a
 * given tenant rents in the building. Unit ids are sorted naturally for stable display.
 */
export function invertUnitTenants(
  unitTenants: Record<string, string[]> | undefined | null,
): Record<string, string[]> {
  const byTenant: Record<string, Set<string>> = {};
  Object.entries(unitTenants || {}).forEach(([unitId, names]) => {
    (names || []).forEach((name) => {
      if (!byTenant[name]) byTenant[name] = new Set();
      byTenant[name].add(unitId);
    });
  });
  return Object.fromEntries(
    Object.entries(byTenant).map(([name, set]) => [
      name,
      Array.from(set).sort((a, b) => a.localeCompare(b, "nl", { numeric: true })),
    ]),
  );
}

/** attributeId → tenant names, derived via each attribute's contractProducts on non-closed contracts. */
export function buildAttributeTenantMap(attributes: any[] | undefined | null): Record<string, string[]> {
  const map: Record<string, Set<string>> = {};
  (attributes || []).forEach((attribute) => {
    const attributeId = attribute?.id;
    if (attributeId == null) return;
    const key = String(attributeId);
    (attribute?.contractProducts || []).forEach((product: any) => {
      const contract = product?.contract;
      if (!contract || !isActivePhase(contract?.phase)) return;
      const names = tenantNamesFromContacts(contract?.contractContacts);
      if (names.length === 0) return;
      if (!map[key]) map[key] = new Set();
      names.forEach((name) => map[key].add(name));
    });
  });
  return setMapToArrays(map);
}

/**
 * Group every placed unit/garage on the plan (all pages) by its tenant.
 *
 * A target placed multiple times (e.g. a pin and a contour, or on several pages) collapses
 * into a single list item; a target with co-tenants appears under each tenant. Placements
 * without an active tenant are collected under {@link NO_TENANT_LABEL} so nothing is hidden.
 * Groups are ordered with the most placements first (so tenants renting several units/garages
 * stand out), and the unassigned group always sits last.
 */
export function groupAnnotationsByTenant(
  annotations: PlanAnnotation[],
  unitTenants: Record<string, string[]>,
  attributeTenants: Record<string, string[]>,
  labelFor: (a: PlanAnnotation) => string,
  colorFor: (a: PlanAnnotation) => string,
): PlanTenantGroup[] {
  // 1. Collapse annotations into one item per placed target (merging shapes + pages).
  const itemByKey = new Map<string, PlanListItem & { tenants: string[] }>();
  annotations.forEach((annotation) => {
    const targetId = annotation.targetType === "unit" ? annotation.unitId : annotation.buildingAttribute?.id;
    if (targetId == null || targetId === "") return;
    const id = String(targetId);
    const key = annKey(annotation);
    const existing = itemByKey.get(key);
    if (existing) {
      if (!existing.shapes.includes(annotation.shape)) existing.shapes.push(annotation.shape);
      existing.page = Math.min(existing.page, annotation.page || 1);
      return;
    }
    const tenants = annotation.targetType === "unit" ? unitTenants[id] || [] : attributeTenants[id] || [];
    itemByKey.set(key, {
      key,
      targetType: annotation.targetType,
      id,
      label: labelFor(annotation),
      color: colorFor(annotation),
      shapes: [annotation.shape],
      page: annotation.page || 1,
      tenants,
    });
  });

  // 2. Bucket items per tenant (an item with co-tenants lands in each tenant's bucket).
  const buckets = new Map<string, PlanListItem[]>();
  const addToBucket = (tenant: string, item: PlanListItem) => {
    if (!buckets.has(tenant)) buckets.set(tenant, []);
    buckets.get(tenant)!.push(item);
  };
  itemByKey.forEach((entry) => {
    const { tenants, ...item } = entry;
    if (tenants.length === 0) addToBucket(NO_TENANT_LABEL, item);
    else tenants.forEach((tenant) => addToBucket(tenant, item));
  });

  // 3. Order groups: most placements first, unassigned last. Each tenant group also carries
  //    every unit it rents in the building (so a garage tenant shows their apartment too).
  const unitsByTenant = invertUnitTenants(unitTenants);
  const groups: PlanTenantGroup[] = Array.from(buckets.entries()).map(([tenant, items]) => ({
    tenant,
    isUnassigned: tenant === NO_TENANT_LABEL,
    items: items.sort((a, b) => a.label.localeCompare(b.label, "nl", { numeric: true })),
    rentedUnits: tenant === NO_TENANT_LABEL ? [] : unitsByTenant[tenant] || [],
  }));
  groups.sort((a, b) => {
    if (a.isUnassigned !== b.isUnassigned) return a.isUnassigned ? 1 : -1;
    if (b.items.length !== a.items.length) return b.items.length - a.items.length;
    return a.tenant.localeCompare(b.tenant, "nl", { numeric: true });
  });
  return groups;
}
